import { axiosMedForNgos } from '../utils/axios.util';
import { computed, reactive, toRefs } from 'vue';
import useSettings from './settings.module';
import localforage from 'localforage';
import { waitFor } from '../utils/methods.util';
import useOrganizations from './organizations.module';

const { notify, getTranslation } = useSettings();
const { setHasBasketOrders } = useOrganizations();

const ONE_SECOND_IN_ms = 1000;
const ONE_MINUTE_IN_ms = 60 * ONE_SECOND_IN_ms;
const TIMER_TICK_INTERVAL_IN_ms = ONE_SECOND_IN_ms;
const ORDER_TIMER_STORE_KEY = 'expirationTimestampInms';
const NOTIFICATION_WARNING_SENT = 'notificationWarningSent';
const NOTIFICATION_WARNING_MINUTES_IN_ms = 10 * ONE_MINUTE_IN_ms;

const orderTimerStore = localforage.createInstance({
    name: 'OrderTimerStore',
});

/** @type {import('vue').UnwrapRef<MedForNgos.OrderTimerState>} */
const orderTimerState = reactive({
    expirationTimestampInms: 0,
    msToOrder: 0,
    timeLeftStringRepresentation: computed(() => {
        const minutes = Math.floor(orderTimerState.msToOrder / ONE_MINUTE_IN_ms);
        const seconds = Number(((orderTimerState.msToOrder % ONE_MINUTE_IN_ms) / ONE_SECOND_IN_ms).toFixed(0));

        return seconds === 60 ? (minutes + 1) + ':00' : minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
    }),
    isOrderTimerActive: computed(() => orderTimerState.msToOrder > 0),
    notificationWarningSent: false,
});

export default function useOrderTimers() {
    const initOrderTimerModule = async() => {
        const expTimestampInmsFromLocalforage = await orderTimerStore.getItem(ORDER_TIMER_STORE_KEY) || 0;

        orderTimerState.notificationWarningSent = await orderTimerStore.getItem(NOTIFICATION_WARNING_SENT) || false;

        if (_hasNotExpired(expTimestampInmsFromLocalforage)) {
            _launchOrderTimer(expTimestampInmsFromLocalforage);
        }
    };

    /** @param {number} expirationTimestampInms */
    const _hasNotExpired = (expirationTimestampInms) => {
        return expirationTimestampInms > Date.now();
    };

    const checkAndSetOrderDeletionScheduleOnLogin = async() => {
        /** @type {import('axios').AxiosResponse<any>? } */
        const response = await axiosMedForNgos.get('/basket-order-schedule');

        const expTimestampInmsFromApi = response?.data?.expiration_timestamp || 0;

        if (_hasNotExpired(expTimestampInmsFromApi)) {
            await orderTimerStore.setItem(ORDER_TIMER_STORE_KEY, expTimestampInmsFromApi);
            _launchOrderTimer(expTimestampInmsFromApi);
        }
    };

    /** @param {number} expirationTimestampInms */
    const _calcmsToOrderFromNow = (expirationTimestampInms) => {
        orderTimerState.msToOrder = expirationTimestampInms - Date.now();
    };

    /** @param {number} expirationTimestampInms */
    const _launchOrderTimer = (expirationTimestampInms) => {
        orderTimerState.expirationTimestampInms = expirationTimestampInms;
        _calcmsToOrderFromNow(expirationTimestampInms);

        _tickLoop();
    };

    /** @param {number} minutesToOrder */
    const triggerOrderTimerWhenUserOrdersFirstProduct = async(minutesToOrder) => {
        const expTimestampInms = Date.now() + (minutesToOrder * ONE_MINUTE_IN_ms);

        await orderTimerStore.setItem(ORDER_TIMER_STORE_KEY, expTimestampInms);

        orderTimerState.notificationWarningSent = false;

        await orderTimerStore.setItem(NOTIFICATION_WARNING_SENT, orderTimerState.notificationWarningSent);

        _launchOrderTimer(expTimestampInms);
    };

    const _tickLoop = async() => {
        while (orderTimerState.isOrderTimerActive) {
            _calcmsToOrderFromNow(orderTimerState.expirationTimestampInms);

            if (orderTimerState.msToOrder <= NOTIFICATION_WARNING_MINUTES_IN_ms && !orderTimerState.notificationWarningSent) {
                _sendWarningNotification();

                orderTimerState.notificationWarningSent = true;

                await orderTimerStore.setItem(NOTIFICATION_WARNING_SENT, orderTimerState.notificationWarningSent);
            }

            if (orderTimerState.msToOrder <= TIMER_TICK_INTERVAL_IN_ms && orderTimerState.isOrderTimerActive) {
                _sendDeleteNotification();

                endOrderTimer();
            }

            await waitFor(TIMER_TICK_INTERVAL_IN_ms);
        }
    };

    const _sendWarningNotification = () => {
        notify({
            text: getTranslation('notifications.warningOrderTimer'),
            status: 'WARNING',
            ms: -1,
        });
    };

    const _sendDeleteNotification = () => {
        notify({
            text: getTranslation('notifications.deleteBasketOrder'),
            status: 'ERROR',
            ms: -1,
        });
    };

    const endOrderTimer = async() => {
        orderTimerState.expirationTimestampInms = 0;
        orderTimerState.msToOrder = 0;
        orderTimerState.notificationWarningSent = false;
        // will use setHasBasketOrders here because of timer expiration possibility
        setHasBasketOrders(false);

        await orderTimerStore.removeItem(ORDER_TIMER_STORE_KEY);
        await orderTimerStore.removeItem(NOTIFICATION_WARNING_SENT);
    };

    const refsOrderTimerStateObject = toRefs(orderTimerState);

    return {
        ...refsOrderTimerStateObject,
        initOrderTimerModule,
        checkAndSetOrderDeletionScheduleOnLogin,
        triggerOrderTimerWhenUserOrdersFirstProduct,
        endOrderTimer,
    };
}
